/**
 * Task #3260 — Resolver origin pubblico per-prodotto lato FE master.
 *
 * I link verso i tenant (login, impersonation) devono puntare al dominio del
 * PRODOTTO del tenant, non sempre al dominio piattaforma. La mappa
 * prodotto→origin vive lato server (env runtime `PRODUCT_PUBLIC_ORIGINS`) ed
 * è esposta al master via `GET /api/master/product-origins` (identity-gated).
 *
 * Fallback fail-safe: prodotto non mappato, fetch fallita o mappa vuota →
 * `https://BRAND_BASE_DOMAIN` (comportamento identico a prima per i tenant
 * easyflow). Mai throw verso i flussi di navigazione.
 */
import { useEffect, useState } from 'react';
import apiClient from '@/lib/api';
import { BRAND_BASE_DOMAIN } from '@easyflow/shared';

export type ProductOriginMap = Readonly<Record<string, string>>;

interface ProductOriginsResponse {
  origins: Record<string, string>;
}

const PLATFORM_ORIGIN = `https://${BRAND_BASE_DOMAIN}`;

let cachedMap: ProductOriginMap | null = null;
let inflight: Promise<ProductOriginMap> | null = null;

async function fetchProductOriginMap(): Promise<ProductOriginMap> {
  try {
    const res = await apiClient.get<ProductOriginsResponse>('/master/product-origins');
    if (res.success && res.data?.origins) {
      cachedMap = { ...res.data.origins };
      return cachedMap;
    }
  } catch {
    // fail-safe: fallback piattaforma, nessun errore verso l'utente
  }
  cachedMap = {};
  return cachedMap;
}

export async function getProductOriginMap(): Promise<ProductOriginMap> {
  if (cachedMap) return cachedMap;
  if (!inflight) {
    inflight = fetchProductOriginMap().finally(() => {
      inflight = null;
    });
  }
  return inflight;
}

/** Origin del tenant dato il suo prodotto, con fallback piattaforma. */
export function originForProduct(
  map: ProductOriginMap,
  product?: string | null,
): string {
  if (product && map[product]) return map[product];
  return PLATFORM_ORIGIN;
}

/** Variante async per i flussi imperativi (impersonation redirect). */
export async function resolveTenantOrigin(product?: string | null): Promise<string> {
  return originForProduct(await getProductOriginMap(), product);
}

/**
 * Hook per il render sincrono (URL login in tabella/wizard): parte dal
 * fallback piattaforma e si aggiorna quando la mappa arriva.
 */
export function useProductOrigins(): (product?: string | null) => string {
  const [map, setMap] = useState<ProductOriginMap>(cachedMap ?? {});
  useEffect(() => {
    let alive = true;
    void getProductOriginMap().then((m) => {
      if (alive) setMap(m);
    });
    return () => {
      alive = false;
    };
  }, []);
  return (product?: string | null) => originForProduct(map, product);
}

/** Solo per i test: azzera la cache modulo. */
export function __resetProductOriginsCacheForTests(): void {
  cachedMap = null;
  inflight = null;
}
